<script setup lang="ts">
  import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist'
  import pdfWorker from '/pdf.worker.min.mjs?url'

  const props = withDefaults(
    defineProps<{
      url: string
      fileName?: string
      maxPages?: number | null
      showDownloadAction?: boolean
      onLoad?: () => void
    }>(),
    {
      fileName: 'document.pdf',
      maxPages: null,
      showDownloadAction: true,
      onLoad: undefined,
    },
  )

  const view = ref<HTMLDivElement>()
  const totalPages = ref(0)
  const renderedPages = ref(0)
  const isLoading = ref(true)
  const loadError = ref(false)

  const normalizedMaxPages = computed(() => {
    if (!props.maxPages || props.maxPages < 1) {
      return null
    }

    return Math.floor(props.maxPages)
  })

  const isTruncated = computed(
    () => normalizedMaxPages.value !== null && totalPages.value > renderedPages.value,
  )

  const showFooter = computed(() => !isLoading.value && !loadError.value && isTruncated.value)

  const downloadPdf = () => {
    downloadFileFromUrl(props.url, props.fileName)
  }

  onMounted(async () => {
    try {
      GlobalWorkerOptions.workerSrc = pdfWorker
      const pdfDoc = await getDocument(props.url).promise
      totalPages.value = pdfDoc.numPages
      const pagesToRender = Math.min(pdfDoc.numPages, normalizedMaxPages.value ?? pdfDoc.numPages)

      for (let i = 1; i <= pagesToRender; i++) {
        const page = await pdfDoc.getPage(i)
        const viewport = page.getViewport({ scale: 4, offsetX: -1, offsetY: -1 })
        const canvas = document.createElement('canvas')
        canvas.style.width = '100%'
        canvas.style.height = 'auto'
        canvas.width = viewport.width - 1
        canvas.height = viewport.height - 1
        // canvas.width = 2480
        // canvas.height = 3508
        // const viewport = page.getViewport({
        //   scale: canvas.width / page.getViewport({ scale: 1 }).width,
        // })

        const context = canvas.getContext('2d')
        if (context) {
          await page.render({
            canvasContext: context,
            viewport,
          }).promise
        }

        const img = document.createElement('img')
        img.src = canvas.toDataURL()
        img.style.width = '100%'
        img.style.height = 'auto'
        img.style.border = '1px solid #e5e7eb' // todo: remove
        img.style.borderRadius = '8px'
        img.style.margin = '0 auto'
        view.value?.append(img)
        renderedPages.value = i
      }
      props.onLoad?.()
    } catch (error) {
      console.error(error)
      loadError.value = true
    } finally {
      isLoading.value = false
    }
  })
</script>

<template>
  <div class="space-y-4">
    <div ref="view" class="flex flex-col gap-4 print:break-before-column" />
    <div
      v-if="loadError"
      class="rounded-md border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700"
    >
      Failed to load PDF preview.
    </div>
    <div
      v-else-if="showFooter"
      class="flex flex-col gap-3 rounded-md border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between"
    >
      <span>Showing first {{ renderedPages }} of {{ totalPages }} pages</span>
      <BasicBtn
        v-if="showDownloadAction"
        color="textual"
        size="small"
        class="self-start sm:self-auto"
        @click="downloadPdf"
      >
        <Icon name="ic:outline-download" class="size-5" />
        Download full document
      </BasicBtn>
    </div>
  </div>
</template>

<style scoped></style>
